<template>
  <vs-wrapper>
    <vs-heading>Coordinates</vs-heading>

    <vs-wrapper direction="horizontal" space="small">
      <vs-text-input
        label="Latitude"
        :value="location.latitude"
        :rules="latitudeRules"
        type="number"
        number-step="0.0000001"
        required
        class="inputCoordinate pr-2"
        :disabled="disabled"
        @input="onLocationUpdated({ field: 'latitude', value: $event })"
      />
      <vs-text-input
        label="Longitude"
        :value="location.longitude"
        :rules="longitudeRules"
        type="number"
        number-step="0.0000001"
        required
        class="inputCoordinate"
        :disabled="disabled"
        @input="onLocationUpdated({ field: 'longitude', value: $event })"
      />
    </vs-wrapper>
    <div v-if="!hideGeofence">
      <vs-text-input
        v-if="showGeofenceField"
        label="Geofence radius (meters)"
        :hint="geofenceHint"
        :value="location.radius"
        :rules="radiusRules"
        type="number"
        :disabled="disabled"
        @input="onLocationUpdated({ field: 'radius', value: $event })"
      >
      </vs-text-input>
      <div v-else>
        <vs-button
          label="Add geofence"
          type="secondary"
          small
          :disabled="disabled"
          @click="geofenceFlag = true"
        >
        </vs-button>

        <v-tooltip top max-width="240">
          <template #activator="{ on }">
            <v-icon class="pl-2" v-on="on">info</v-icon>
          </template>
          <span> A perimeter around the location that knows when you enter or leave it </span>
        </v-tooltip>
      </div>
    </div>
  </vs-wrapper>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import LocationDTO from '@/models/location'
import { integerRange, latitudeRange, longitudeRange } from '@/utils/constants'

const props = withDefaults(
  defineProps<{
    location: LocationDTO
    hideGeofence?: boolean
  }>(),
  {
    hideGeofence: false,
  },
)
const geofenceFlag = ref(false)
const latitudeRules = [
  (v: string) =>
    (parseFloat(v) < latitudeRange.min || parseFloat(v) > latitudeRange.max) &&
    'Latitude must be between -90 and 90',
]
const longitudeRules = [
  (v: string) =>
    (parseFloat(v) < longitudeRange.min || parseFloat(v) > longitudeRange.max) &&
    'Longitude must be between -180 and 180',
]
const radiusRules = [
  (v: string) => parseFloat(v) < 75 && `Radius must be 75 meters or greater`,
  (v: string) =>
    parseFloat(v) >= 75 &&
    parseFloat(v) <= integerRange.max &&
    !Number.isInteger(parseFloat(v)) &&
    `Please enter a whole number`,
  (v: string) => parseFloat(v) > integerRange.max && `Please enter a smaller radius`,
]

const geofenceHint = computed(() => {
  if (props.location.radius && props.location.radius < 200) {
    return 'Going below 200 meters may cause geofence inaccuracies'
  }
  return ''
})
const showGeofenceField = computed(() => geofenceFlag.value || props.location.radius !== null)
const disabled = computed(() => !props.location.canEdit)
const emit = defineEmits(['updated'])

function onLocationUpdated(data: { field: string; value: any }) {
  emit('updated', data)
}
</script>

<style scoped></style>
